import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search, Bell, Sun, Moon, ChevronRight,
  User, LogOut, Settings, StickyNote, X, Megaphone,
  Ticket, Users2, Package, BookOpen, FileText, Building2,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAppStore } from '@/store/app'
import { notificationsApi } from '@/api/notifications'
import { keycloakLogout } from '@/hooks/useKeycloak'
import { useRole } from '@/hooks/useRole'
import { QuickNoteDrawer } from '@/components/notes/QuickNoteDrawer'
import { WhatsNewPanel } from '@/components/whats-new/WhatsNewPanel'
import { useWhatsNew } from '@/hooks/useWhatsNew'

const BREADCRUMB_LABELS: Record<string, string> = {
  '':               'Home',
  waste:            'Waste Management',
  tickets:          'Tickets',
  clients:          'Clients',
  invoices:         'Invoices',
  assets:           'Assets',
  contracts:        'Contracts',
  kb:               'Knowledge Base',
  admin:            'Admin',
  users:            'Users & Teams',
  audit:            'Audit Logs',
  billing:          'Billing',
  auth:             'Enterprise Auth',
  branding:         'Branding',
  'ai-provider':    'AI Provider',
  settings:         'Settings',
  profile:          'Profile',
  profiles:         'Flavours',
  onboarding:       'Organization Setup',
  register:         'Create Account',
  'purchase-orders':'Purchase Orders',
  'live-chat':      'Live Chat',
  scheduled:        'Scheduled Tickets',
  tasks:            'Work Management',
  planning:         'Planning',
  timesheets:       'Timesheets',
  approvals:        'Approvals',
  notes:            'Notes',
  notifications:    'Asset Settings',
  'whats-new':      'What’s New',
}

// Quick-nav items shown in the search overlay when no query is typed
const QUICK_NAV = [
  { label: 'Tickets',        path: '/tickets',     icon: Ticket    },
  { label: 'Clients',        path: '/clients',     icon: Users2    },
  { label: 'Assets',         path: '/assets',      icon: Package   },
  { label: 'Knowledge Base', path: '/kb',          icon: BookOpen  },
  { label: 'Invoices',       path: '/invoices',    icon: FileText  },
  { label: 'Organization',   path: '/admin/organization', icon: Building2 },
]

function SearchOverlay({ onClose }: { onClose: () => void }) {
  const { isWasteRestricted } = useRole()
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    inputRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const quickNav = isWasteRestricted ? [{ label: 'Waste Management', path: '/waste', icon: Package }] : QUICK_NAV
  const go = (path: string) => { navigate(path); onClose() }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.12 }}
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[12vh] px-4"
      onClick={onClose}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <motion.div
        initial={{ opacity: 0, y: -12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -12, scale: 0.97 }}
        transition={{ duration: 0.15 }}
        className="relative w-full max-w-xl bg-bg-overlay border border-border-default rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border-default">
          <Search className="w-4 h-4 text-text-tertiary flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={isWasteRestricted ? 'Search navigation…' : 'Search tickets, clients, assets, invoices…'}
            className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-text-tertiary hover:text-text-secondary">
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:flex items-center text-[10px] text-text-tertiary bg-bg-subtle border border-border-default rounded px-1.5 py-0.5 font-mono flex-shrink-0">
            Esc
          </kbd>
        </div>

        {/* Body */}
        <div className="p-2 max-h-[60vh] overflow-y-auto">
          {!query ? (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary px-3 py-2">
                Quick navigate
              </p>
              {quickNav.map(item => (
                <button key={item.path} onClick={() => go(item.path)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-bg-subtle text-left transition-colors group">
                  <div className="w-7 h-7 rounded-lg bg-bg-subtle group-hover:bg-bg-muted border border-border-default flex items-center justify-center flex-shrink-0 transition-colors">
                    <item.icon className="w-3.5 h-3.5 text-text-secondary" />
                  </div>
                  <span className="text-sm text-text-primary">{item.label}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-text-tertiary ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </>
          ) : (
            <div className="px-3 py-8 text-center">
              <Search className="w-8 h-8 text-text-tertiary mx-auto mb-3 opacity-40" />
              <p className="text-sm text-text-secondary">
                {isWasteRestricted ? 'Use the Waste Management navigation link.' : 'Full-text search across all modules is coming soon.'}
              </p>
              <p className="text-xs text-text-tertiary mt-1">
                Use the quick-navigate links above for now.
              </p>
            </div>
          )}
        </div>

        <div className="px-4 py-2 border-t border-border-default flex items-center gap-4 text-[11px] text-text-tertiary">
          <span className="flex items-center gap-1"><kbd className="font-mono bg-bg-subtle border border-border-default rounded px-1">↑↓</kbd> navigate</span>
          <span className="flex items-center gap-1"><kbd className="font-mono bg-bg-subtle border border-border-default rounded px-1">↵</kbd> open</span>
          <span className="flex items-center gap-1"><kbd className="font-mono bg-bg-subtle border border-border-default rounded px-1">Esc</kbd> close</span>
        </div>
      </motion.div>
    </motion.div>
  )
}

export function TopBar() {
  const location = useLocation()
  const navigate  = useNavigate()
  const { theme, toggleTheme, notifications, user, token, clearAuth, setNotifications, markRead, markAllRead } = useAppStore()
  const { label: roleLabel, isWasteRestricted, canAccessPath } = useRole()

  const [showSearch, setShowSearch]           = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showUserMenu, setShowUserMenu]       = useState(false)
  const [quickNoteOpen, setQuickNoteOpen]     = useState(false)
  const [showWhatsNew, setShowWhatsNew]       = useState(false)
  const { unseenCount: whatsNewUnseen }       = useWhatsNew()
  const notifRef = useRef<HTMLDivElement>(null)
  const whatsNewRef = useRef<HTMLDivElement>(null)
  const userRef  = useRef<HTMLDivElement>(null)

  const openSearch  = useCallback(() => setShowSearch(true),  [])
  const closeSearch = useCallback(() => setShowSearch(false), [])

  const handleLogout = async () => {
    setShowUserMenu(false)
    clearAuth()
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('organizationId')
    if (import.meta.env.VITE_KEYCLOCK_URL && import.meta.env.VITE_BYPASS_AUTH !== 'true') {
      try { await keycloakLogout() } catch {
        window.location.assign(import.meta.env.VITE_BASE_APP_URL || window.location.origin)
      }
      return
    }
    navigate('/login', { replace: true })
  }

  const segments = location.pathname.split('/').filter(Boolean)
  const formatBreadcrumbSegment = (seg: string) => {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return 'Detail'
    return BREADCRUMB_LABELS[seg] ?? seg.charAt(0).toUpperCase() + seg.slice(1)
  }
  const breadcrumbs = [
    { label: 'Home', path: '/' },
    ...segments.map((seg, i) => ({
      label: formatBreadcrumbSegment(seg),
      path: '/' + segments.slice(0, i + 1).join('/'),
    }))
  ]

  // ⌘K / Ctrl+K → open search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowSearch(v => !v)
      }
      if (!isWasteRestricted && (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'n') {
        e.preventDefault()
        setQuickNoteOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isWasteRestricted])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifications(false)
      if (userRef.current  && !userRef.current.contains(e.target as Node))  setShowUserMenu(false)
      if (whatsNewRef.current && !whatsNewRef.current.contains(e.target as Node)) setShowWhatsNew(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const refreshNotifications = async () => {
    if (!token || isWasteRestricted) return
    try {
      const res = await notificationsApi.list({ limit: 20 })
      setNotifications(res.data ?? [])
    } catch { /* keep existing list */ }
  }

  useEffect(() => {
    refreshNotifications()
    if (!token || isWasteRestricted) return
    const timer = window.setInterval(refreshNotifications, 45_000)
    return () => window.clearInterval(timer)
  }, [token, isWasteRestricted])

  const handleMarkRead = async (id: string, link?: string) => {
    markRead(id)
    try { await notificationsApi.markRead(id) } catch { refreshNotifications() }
    if (link && canAccessPath(link)) { setShowNotifications(false); navigate(link) }
  }

  const handleMarkAllRead = async () => {
    markAllRead()
    try { await notificationsApi.markAllRead() } catch { refreshNotifications() }
  }

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <>
      <header className="h-14 flex items-center gap-3 px-4 bg-bg-base border-b border-border-default flex-shrink-0">

        {/* Left — breadcrumbs */}
        <nav className="flex items-center gap-1 text-sm flex-1 min-w-0 overflow-hidden">
          {breadcrumbs.map((crumb, i) => (
            <span key={crumb.path} className="flex items-center gap-1 min-w-0">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />}
              {i === breadcrumbs.length - 1 ? (
                <span className="truncate text-text-primary font-medium">{crumb.label}</span>
              ) : (
                <Link to={crumb.path} className="truncate text-text-secondary hover:text-text-primary transition-colors whitespace-nowrap">
                  {crumb.label}
                </Link>
              )}
            </span>
          ))}
        </nav>

        {/* Centre — Search */}
        <button
          onClick={openSearch}
          className="hidden md:flex items-center gap-2.5 w-64 xl:w-80 px-3 py-2 rounded-xl bg-bg-subtle border border-border-default hover:border-brand-500/50 hover:bg-bg-elevated transition-all text-left group"
        >
          <Search className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0 group-hover:text-brand-500 transition-colors" />
          <span className="flex-1 text-sm text-text-tertiary truncate">{isWasteRestricted ? 'Search Waste Management…' : 'Search tickets, clients, assets…'}</span>
          <kbd className="flex items-center gap-0.5 text-[10px] text-text-tertiary bg-bg-muted border border-border-default rounded px-1.5 py-0.5 font-mono flex-shrink-0">
            ⌘K
          </kbd>
        </button>

        {/* Mobile search icon */}
        <button onClick={openSearch}
          className="md:hidden p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-subtle border border-transparent hover:border-border-default transition-colors">
          <Search className="w-4 h-4" />
        </button>

        {/* Right — utility controls */}
        <div className="flex items-center gap-1 flex-shrink-0">

          {/* Quick Note */}
          {!isWasteRestricted && <>
          <button onClick={() => setQuickNoteOpen(true)}
            className="hidden sm:flex p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-subtle border border-transparent hover:border-border-default transition-colors"
            title="Quick Note (⌘⇧N)">
            <StickyNote className="w-4 h-4" />
          </button>

          {/* What's new — product updates, kept apart from work notifications */}
          <div className="relative" ref={whatsNewRef}>
            <button onClick={() => setShowWhatsNew(v => !v)}
              className="relative p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-subtle border border-transparent hover:border-border-default transition-colors"
              title="What’s new" aria-label={whatsNewUnseen > 0 ? `What’s new, ${whatsNewUnseen} unread` : 'What’s new'}>
              <Megaphone className="w-4 h-4" />
              {whatsNewUnseen > 0 && (
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-brand-500 rounded-full" />
              )}
            </button>
            <AnimatePresence>
              {showWhatsNew && <WhatsNewPanel onClose={() => setShowWhatsNew(false)} />}
            </AnimatePresence>
          </div>

          </>}
          {/* Theme toggle */}
          <button onClick={toggleTheme}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-subtle border border-transparent hover:border-border-default transition-colors"
            title="Toggle theme">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Notifications */}
          {!isWasteRestricted && <div className="relative" ref={notifRef}>
            <button onClick={() => setShowNotifications(v => !v)}
              className="relative p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-subtle border border-transparent hover:border-border-default transition-colors">
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-status-error rounded-full" />
              )}
            </button>
            <AnimatePresence>
              {showNotifications && (
                <NotificationPanel onMarkRead={handleMarkRead} onMarkAllRead={handleMarkAllRead} />
              )}
            </AnimatePresence>
          </div>}

          {/* Divider */}
          <div className="w-px h-5 bg-border-default mx-1" />

          {/* User menu */}
          <div className="relative" ref={userRef}>
            <button onClick={() => setShowUserMenu(v => !v)}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-bg-subtle transition-colors">
              <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-semibold select-none">
                {user?.name?.charAt(0)?.toUpperCase() ?? 'A'}
              </div>
            </button>
            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.96 }}
                  transition={{ duration: 0.1 }}
                  className="absolute right-0 top-full mt-2 w-52 bg-bg-overlay border border-border-default rounded-xl shadow-lg overflow-hidden z-50"
                >
                  <div className="px-3 py-2.5 border-b border-border-default">
                    <p className="text-sm font-semibold text-text-primary truncate">{user?.name ?? 'Admin'}</p>
                    <p className="text-xs text-text-tertiary truncate">{user?.email ?? ''}</p>
                    {user && (
                      <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-brand-500/15 text-brand-400 font-medium">
                        {roleLabel}
                      </span>
                    )}
                  </div>
                  <div className="p-1">
                    {!isWasteRestricted && <><MenuButton icon={User}     label="Profile"   onClick={() => { setShowUserMenu(false); navigate('/profile') }} />
                    <MenuButton icon={Settings} label="Settings"  onClick={() => { setShowUserMenu(false); navigate('/settings') }} />
                    <MenuButton icon={Megaphone} label="What’s new" onClick={() => { setShowUserMenu(false); navigate('/whats-new') }} />
                    </>}<div className="border-t border-border-default my-1" />
                    <MenuButton icon={LogOut}   label="Sign out"  variant="danger" onClick={handleLogout} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Search overlay — rendered outside header so it covers the full viewport */}
      <AnimatePresence>
        {showSearch && <SearchOverlay onClose={closeSearch} />}
      </AnimatePresence>

      {!isWasteRestricted && <QuickNoteDrawer open={quickNoteOpen} onClose={() => setQuickNoteOpen(false)} />}
    </>
  )
}

function NotificationPanel({
  onMarkRead, onMarkAllRead,
}: {
  onMarkRead: (id: string, link?: string) => void
  onMarkAllRead: () => void
}) {
  const { notifications } = useAppStore()

  const formatTime = (createdAt?: string) => {
    if (!createdAt) return ''
    const diff = Date.now() - new Date(createdAt).getTime()
    const minutes = Math.max(0, Math.floor(diff / 60000))
    if (minutes < 1)  return 'Just now'
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24)   return `${hours} hour${hours === 1 ? '' : 's'} ago`
    const days = Math.floor(hours / 24)
    return `${days} day${days === 1 ? '' : 's'} ago`
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ duration: 0.15 }}
      className="absolute right-0 top-full mt-2 w-80 bg-bg-overlay border border-border-default rounded-xl shadow-xl overflow-hidden z-50"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default">
        <h3 className="text-sm font-semibold text-text-primary">Notifications</h3>
        <button onClick={onMarkAllRead} className="text-xs text-brand-500 hover:text-brand-400 transition-colors">
          Mark all read
        </button>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-text-tertiary">No notifications</div>
        ) : notifications.map(n => (
          <button key={n.id} onClick={() => onMarkRead(n.id, n.link)}
            className={cn(
              'w-full flex items-start gap-3 px-4 py-3 hover:bg-bg-subtle cursor-pointer transition-colors text-left',
              !n.read && 'bg-brand-500/5'
            )}>
            <div className={cn(
              'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
              n.type === 'error'   && 'bg-status-error',
              n.type === 'warning' && 'bg-status-warning',
              n.type === 'success' && 'bg-status-success',
              n.type === 'info'    && 'bg-status-info',
            )} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-text-primary truncate">{n.title}</p>
              {n.message && <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{n.message}</p>}
              <p className="text-xs text-text-tertiary mt-0.5">{formatTime(n.createdAt)}</p>
            </div>
            {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0 mt-2" />}
          </button>
        ))}
      </div>
    </motion.div>
  )
}

function MenuButton({ icon: Icon, label, variant, onClick }: {
  icon: React.ElementType; label: string; variant?: 'danger'; onClick?: () => void
}) {
  return (
    <button onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm transition-colors',
        variant === 'danger'
          ? 'text-status-error hover:bg-status-error/10'
          : 'text-text-secondary hover:text-text-primary hover:bg-bg-subtle'
      )}>
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}
